// Aurora hooks for project reports with corporate styling
import { useQuery } from '@tanstack/react-query';
import { projectReportApi } from '@/lib/client';

interface UseReportsParams {
    projectId?: string;
    status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    type?: 'PROGRESS' | 'MILESTONE' | 'FINAL' | 'ISSUE';
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: 'createdAt' | 'publishedAt' | 'reportDate' | 'title';
    sortOrder?: 'asc' | 'desc';
}

export const useReports = (params: UseReportsParams = {}) => {
    return useQuery({
        queryKey: ['reports', params],
        queryFn: async () => {
            try {
                const res = await projectReportApi.getReports({
                    projectId: params.projectId,
                    status: 'PUBLISHED', // Always published for public website
                    type: params.type,
                    search: params.search,
                    page: params.page,
                    limit: params.limit,
                    sortBy: params.sortBy,
                    sortOrder: params.sortOrder,
                });
                console.log('Aurora Reports API response:', res);
                return res;
            } catch (error) {
                console.error('Aurora Reports API error:', error);
                throw error;
            }
        },
        retry: 1,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

export const useReportBySlug = (slug: string) => {
    return useQuery({
        queryKey: ['report', slug],
        queryFn: () => projectReportApi.getReportBySlug(slug),
        enabled: !!slug,
        retry: 1,
        staleTime: 5 * 60 * 1000,
    });
};

export const useRecentReports = (limit: number = 10) => {
    return useQuery({
        queryKey: ['recent-reports', limit],
        queryFn: async () => {
            try {
                const res = await projectReportApi.getRecentReports({ limit });
                console.log('Aurora Recent reports API response:', res);
                return res;
            } catch (error) {
                console.error('Aurora Recent reports API error:', error);
                throw error;
            }
        },
        retry: 1,
        staleTime: 5 * 60 * 1000,
    });
};

export const useReportsByProject = (projectId: string, params: { page?: number; limit?: number } = {}) => {
    return useQuery({
        queryKey: ['reports-by-project', projectId, params],
        queryFn: () => projectReportApi.getReportsByProject(projectId, params),
        enabled: !!projectId,
        retry: 1,
        staleTime: 5 * 60 * 1000,
    });
};

export const useReportStats = () => {
    return useQuery({
        queryKey: ['report-stats'],
        queryFn: async () => {
            try {
                const res = await projectReportApi.getReportStats();
                console.log('Aurora Report stats API response:', res);
                return res;
            } catch (error) {
                console.error('Aurora Report stats API error:', error);
                throw error;
            }
        },
        retry: 1,
        staleTime: 10 * 60 * 1000, // 10 minutes
    });
};
